import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMCPStore } from '../store/mcp';
import { useWorkflowStore } from '../store/workflow';
import { createApiClient, type N8nWorkflowSummary } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../lib/api-config';

const STORAGE_KEY = 'import-instance-id';

// Icon Components
function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );
}

function CubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  );
}

function ChevronUpDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

type SortOption = 'name-asc' | 'name-desc' | 'date-asc' | 'date-desc';

interface ImportResult {
  type: 'success' | 'error';
  message: string;
  imported?: number;
  skipped?: number;
}

export function ImportWorkflowsPage() {
  const { instances, fetchInstances, isLoading: instancesLoading, instancesLastFetchedAt } = useMCPStore();
  const navigate = useNavigate();

  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const [workflows, setWorkflows] = useState<N8nWorkflowSummary[]>([]);
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');

  const selectedInstance = instances.find((i) => i.id === selectedInstanceId) ?? null;

  const getApiClient = () => {
    return createApiClient(API_BASE_URL);
  };

  const loadWorkflows = async (instanceId: string | null) => {
    if (!instanceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const apiClient = getApiClient();
      const result = await apiClient.listN8nWorkflows(instanceId);
      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.data) {
        if (!result.data.success) {
          setError(result.data.error || 'Failed to load workflows');
          return;
        }
        setWorkflows(result.data.workflows);
        setImportedIds(new Set(result.data.importedIds));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch instances on mount
  useEffect(() => {
    fetchInstances();
  }, []);

  // Once instances are loaded, resolve the selected instance
  useEffect(() => {
    if (instances.length === 0) {
      if (!instancesLoading) setIsLoading(false);
      return;
    }

    // Already selected a valid instance — nothing to do
    if (selectedInstanceId && instances.some((i) => i.id === selectedInstanceId)) return;

    // Try localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && instances.some((i) => i.id === stored)) {
      setSelectedInstanceId(stored);
      return;
    }

    // Default to first instance
    const first = instances[0];
    if (first) setSelectedInstanceId(first.id);
  }, [instances, instancesLoading]);

  // Load workflows whenever selectedInstanceId changes
  useEffect(() => {
    if (selectedInstanceId) {
      // Reset UI state for new instance
      setWorkflows([]);
      setImportedIds(new Set());
      setSelectedIds(new Set());
      setSearchQuery('');
      loadWorkflows(selectedInstanceId);
    }
  }, [selectedInstanceId]);

  // Close picker on outside click
  useEffect(() => {
    if (!showPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPicker]);

  // Auto-dismiss import result
  useEffect(() => {
    if (importResult) {
      const timer = setTimeout(() => setImportResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [importResult]);

  const handleSelectInstance = (instanceId: string) => {
    setShowPicker(false);
    setSelectedInstanceId(instanceId);
    localStorage.setItem(STORAGE_KEY, instanceId);
  };

  const handleToggleSelect = (id: string) => {
    if (importedIds.has(id)) return;

    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    const selectableIds = filteredWorkflows
      .filter((w) => !importedIds.has(w.id))
      .map((w) => w.id);

    if (allSelected) {
      const newSelected = new Set(selectedIds);
      selectableIds.forEach((id) => newSelected.delete(id));
      setSelectedIds(newSelected);
    } else {
      const newSelected = new Set(selectedIds);
      selectableIds.forEach((id) => newSelected.add(id));
      setSelectedIds(newSelected);
    }
  };

  const handleImport = async () => {
    if (selectedIds.size === 0) return;

    setIsImporting(true);
    setImportResult(null);

    try {
      const apiClient = getApiClient();
      const result = await apiClient.importN8nWorkflows(
        Array.from(selectedIds),
        selectedInstanceId ?? undefined
      );

      if (result.error) {
        setImportResult({ type: 'error', message: result.error });
        return;
      }

      if (result.data) {
        if (!result.data.success) {
          setImportResult({ type: 'error', message: result.data.error || 'Import failed' });
          return;
        }

        const importedCount = result.data.imported.length;
        const skippedCount = result.data.skipped.length;

        const newImportedIds = new Set(importedIds);
        result.data.imported.forEach((item) => newImportedIds.add(item.n8nId));
        setImportedIds(newImportedIds);
        setSelectedIds(new Set());

        setImportResult({
          type: 'success',
          message: `Successfully imported ${importedCount} workflow${importedCount !== 1 ? 's' : ''}`,
          imported: importedCount,
          skipped: skippedCount,
        });

        if (importedCount > 0) {
          useWorkflowStore.getState().invalidateWorkflows();
        }
      }
    } catch (err) {
      setImportResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'Import failed',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const dismissResult = () => setImportResult(null);

  // Filter and sort workflows
  const filteredWorkflows = workflows
    .filter((w) => w.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'date-asc':
          return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        case 'date-desc':
        default:
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
    });

  const selectableCount = filteredWorkflows.filter((w) => !importedIds.has(w.id)).length;
  const allSelected = selectableCount > 0 && selectedIds.size === selectableCount;

  // Wait until instances have been fetched at least once before deciding
  const instancesFetched = instancesLastFetchedAt !== null || instances.length > 0;

  // No instances — show empty state (only after we've actually fetched)
  if (instancesFetched && !instancesLoading && instances.length === 0) {
    return (
      <div className="min-h-full bg-gray-50 p-4 md:p-6">
        <header className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <DownloadIcon className="h-4 w-4" />
            <span>Tools</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Import Workflows</h1>
          <p className="mt-1 text-gray-500">Import existing workflows from your n8n instance</p>
        </header>

        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100">
            <LinkIcon className="h-7 w-7 text-gray-400" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No n8n instance connected</h3>
          <p className="mt-1 text-center text-gray-500 max-w-md">
            Connect to your n8n instance to import your existing workflows into the builder.
          </p>
          <Link
            to="/connect"
            className="mt-6 flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700"
          >
            <LinkIcon className="h-4 w-4" />
            Connect to n8n
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <DownloadIcon className="h-4 w-4" />
          <span>Tools</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Import Workflows</h1>
            <p className="mt-1 text-gray-500">Import existing workflows from your n8n instance</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Instance Selector */}
            <div className="relative" ref={pickerRef}>
              <button
                onClick={() => setShowPicker(!showPicker)}
                disabled={isImporting}
                className="flex items-center gap-2.5 rounded-lg border px-3.5 py-2 text-sm font-semibold transition-all hover:shadow-sm disabled:opacity-50"
                style={{
                  borderColor: selectedInstance?.color ? `${selectedInstance.color}40` : '#e5e7eb',
                  backgroundColor: selectedInstance?.color ? `${selectedInstance.color}0a` : '#fff',
                  color: '#374151',
                }}
              >
                <span
                  className="inline-block h-3 w-3 rounded-full ring-2 ring-white"
                  style={{ backgroundColor: selectedInstance?.color || '#9ca3af' }}
                />
                <span className="max-w-[140px] truncate">
                  {selectedInstance?.name || 'Select instance'}
                </span>
                <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
              </button>

              {showPicker && (
                <div className="absolute right-0 z-20 mt-1.5 w-60 rounded-xl border border-gray-200 bg-white py-1.5 shadow-xl">
                  {instances.map((inst) => (
                    <button
                      key={inst.id}
                      onClick={() => handleSelectInstance(inst.id)}
                      className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm transition-colors ${
                        inst.id === selectedInstanceId
                          ? 'font-semibold text-gray-900 bg-gray-50'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span
                        className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: inst.color || '#9ca3af' }}
                      />
                      <span className="flex-1 truncate">{inst.name}</span>
                      {inst.id === selectedInstanceId && (
                        <CheckIcon className="h-4 w-4 text-primary-600" />
                      )}
                    </button>
                  ))}
                  <div className="border-t border-gray-100 mt-1.5 pt-1">
                    <button
                      onClick={() => {
                        setShowPicker(false);
                        navigate('/connect');
                      }}
                      className="flex w-full items-center px-3.5 py-2.5 text-left text-sm text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                    >
                      Manage instances
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => loadWorkflows(selectedInstanceId)}
              disabled={isLoading || !selectedInstanceId}
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleImport}
              disabled={selectedIds.size === 0 || isImporting}
              className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              <DownloadIcon className="h-4 w-4" />
              {isImporting ? 'Importing...' : `Import Selected (${selectedIds.size})`}
            </button>
          </div>
        </div>
      </header>

      {/* Import Result Toast */}
      {importResult && (
        <div className="fixed right-6 top-6 z-50 toast-enter">
          <div
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 shadow-lg ${
              importResult.type === 'success'
                ? 'border-green-200 bg-green-50'
                : 'border-red-200 bg-red-50'
            }`}
          >
            <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
              importResult.type === 'success' ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {importResult.type === 'success' ? (
                <CheckIcon className="h-4 w-4 text-green-600" />
              ) : (
                <XMarkIcon className="h-4 w-4 text-red-600" />
              )}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                importResult.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {importResult.message}
              </p>
              {importResult.skipped && importResult.skipped > 0 && (
                <p className="text-sm text-green-600">
                  {importResult.skipped} skipped (already imported)
                </p>
              )}
            </div>
            <button
              onClick={dismissResult}
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
              <XMarkIcon className="h-4 w-4 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button
              onClick={() => loadWorkflows(selectedInstanceId)}
              className="flex items-center gap-2 rounded-lg bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-200"
            >
              <RefreshIcon className="h-4 w-4" />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Search and Sort Toolbar */}
      {!isLoading && workflows.length > 0 && (
        <div className="mb-4 flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="appearance-none rounded-lg border border-gray-300 bg-white py-2 pl-4 pr-10 text-sm font-medium text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="date-desc">Newest first</option>
              <option value="date-asc">Oldest first</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
            </select>
            <ChevronUpDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 rounded-xl skeleton" />
          ))}
        </div>
      ) : workflows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100">
            <CubeIcon className="h-7 w-7 text-gray-400" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No workflows found</h3>
          <p className="mt-1 text-center text-gray-500">
            Your n8n instance doesn't have any workflows yet.
          </p>
        </div>
      ) : filteredWorkflows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100">
            <SearchIcon className="h-7 w-7 text-gray-400" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No matching workflows</h3>
          <p className="mt-1 text-center text-gray-500">
            No workflows match "{searchQuery}". Try a different search term.
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
                disabled={selectableCount === 0}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              />
            </div>
            <div className="flex-1 text-sm font-medium text-gray-700">Workflow Name</div>
            <div className="w-24 text-sm font-medium text-gray-700">Status</div>
            <div className="w-32 text-sm font-medium text-gray-700 text-right">Updated</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-100">
            {filteredWorkflows.map((workflow) => {
              const isImported = importedIds.has(workflow.id);
              const isSelected = selectedIds.has(workflow.id);

              return (
                <div
                  key={workflow.id}
                  onClick={() => handleToggleSelect(workflow.id)}
                  className={`flex items-center gap-4 px-4 py-3 transition-colors ${
                    isImported
                      ? 'bg-gray-50 cursor-not-allowed'
                      : isSelected
                      ? 'bg-primary-50 cursor-pointer'
                      : 'hover:bg-gray-50 cursor-pointer'
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleSelect(workflow.id)}
                      disabled={isImported}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium truncate ${isImported ? 'text-gray-500' : 'text-gray-900'}`}>
                        {workflow.name}
                      </span>
                      {workflow.active && (
                        <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                          Active
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-24">
                    {isImported ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
                        <CheckIcon className="h-3 w-3" />
                        Imported
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">Available</span>
                    )}
                  </div>
                  <div className="w-32 text-sm text-gray-500 text-right">
                    {new Date(workflow.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
